// src/services/protocolCompareService.js
//
// Protocol Compare (study page, admins only): the client half. The PDF upload
// goes to the feature's own Storage folder; the compareProtocols Cloud Function
// reads both PDFs (Document AI + PDFium, never mupdf), builds the redline and,
// when switched on, the AI summary. Everything it writes is its own: the
// protocolComparisons collection and protocolCompare/... in Storage.
//
// New versions are added by protocolVersionService (addProtocolVersion), which
// starts the comparison against the previous version.

import { db, storage, auth } from "../firebase";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import { updateStudy } from "./studyService";
import { logProtocolEvent } from "./protocolVersionService";
import { ref, uploadBytes } from "firebase/storage";
import { getActiveTenantId, getActiveSiteId } from "./activeTenant";
import { getProtocolComparisonsPath } from "./firestorePaths";
import { buildSummaryPdf, buildRedlinePdf, comparePdfFileName } from "./protocolComparePdf";

const sanitize = (name) => String(name || "protocol.pdf").replace(/[^\w.-]/g, "_");
// Google's limit for one Document AI request; checked here so the admin hears it at once.
export const MAX_PDF_BYTES = 40 * 1024 * 1024;

const callFn = async (name, body) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  const idToken = await user.getIdToken();
  const resp = await fetch(`${FUNCTIONS_BASE}/${name}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body || {}),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `${name} failed (${resp.status}).`);
  return data;
};

/** Live list of the study's comparisons, newest first. Returns an unsubscribe fn. */
export const listenComparisons = (studyKey, onData, onError) =>
  onSnapshot(
    query(collection(db, getProtocolComparisonsPath(studyKey)), orderBy("createdAt", "desc")),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => onError && onError(err)
  );

/** Re-run a comparison that failed, with the same two PDFs. */
export const retryComparison = (studyKey, comparisonId) =>
  callFn("compareProtocols", { tenantId: getActiveTenantId(), siteId: getActiveSiteId(), studyKey, comparisonId, retry: true });

/**
 * One of a comparison's files ("redline" | "old" | "new"), as a Blob. Served by
 * the protocolCompareFile function (admins of the site only): the main bucket
 * has no browser (CORS) setting, so getBlob can't read it, and this makes no
 * lasting download link either.
 */
const fetchComparisonFile = async (studyKey, comparisonId, what) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  const idToken = await user.getIdToken();
  const resp = await fetch(`${FUNCTIONS_BASE}/protocolCompareFile`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ tenantId: getActiveTenantId(), siteId: getActiveSiteId(), studyKey, comparisonId, what }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `Couldn't load the file (${resp.status}).`);
  }
  return resp.blob();
};

/** The saved redline (JSON). */
export const loadRedline = async (studyKey, comparisonId) =>
  JSON.parse(await (await fetchComparisonFile(studyKey, comparisonId, "redline")).text());

/** Open the comparison's old or new PDF in a new tab, from memory (no lasting link). */
export const openComparisonPdf = async (studyKey, comparisonId, which) => {
  const tab = window.open("", "_blank");
  try {
    const blob = await fetchComparisonFile(studyKey, comparisonId, which);
    const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    if (tab) tab.location.href = url;
    else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    return true;
  } catch (err) {
    if (tab) tab.close();
    throw new Error(err.message || "Couldn't open the PDF. Please try again.");
  }
};

// ---- AI steps (Claude only; the server refuses while its AI switch is off) ----
const callAi = (studyKey, comparisonId, extra) =>
  callFn("protocolCompareAi", { tenantId: getActiveTenantId(), siteId: getActiveSiteId(), studyKey, comparisonId, ...extra });

/** Write (or rewrite) the AI summary of a finished comparison. Settles when done. */
export const runAiSummary = (studyKey, comparisonId) => callAi(studyKey, comparisonId, { action: "summarize" });

/**
 * Upload the sponsor's summary of changes and check the redline + AI summary
 * against it. Returns { done } (settles when the check finishes); the page
 * follows the comparison doc's `check`.
 */
export const uploadAndCheck = async (studyKey, comparisonId, file) => {
  if (!file) throw new Error("Choose the sponsor's summary of changes (PDF).");
  if (!/\.pdf$/i.test(file.name || "") && file.type !== "application/pdf") throw new Error("The sponsor's summary must be a PDF.");
  if (file.size > 200 * 1024 * 1024) throw new Error("This PDF is over 200 MB. Upload just the summary-of-changes pages.");
  const socPath = `protocolCompare/${getActiveTenantId()}/${getActiveSiteId()}/${studyKey}/uploads/${comparisonId}/soc_${Date.now()}_${sanitize(file.name)}`;
  await uploadBytes(ref(storage, socPath), file, { contentType: "application/pdf" });
  return { done: callAi(studyKey, comparisonId, { action: "check", socPath, socFileName: file.name || "" }) };
};

/** The finished check's item list (saved beside the redline). */
export const loadCheckResult = async (studyKey, comparisonId) =>
  JSON.parse(await (await fetchComparisonFile(studyKey, comparisonId, "check")).text());

// ---- Download as PDF (made in the browser from what the pop-up shows) ----
const me = () => String(auth.currentUser?.email || "").toLowerCase();

export const downloadSummaryPdf = (study, comparison) =>
  buildSummaryPdf({ study, comparison, generatedBy: me() }).save(comparePdfFileName("summary", study));

export const downloadRedlinePdf = (study, comparison, redline, onlyChanged) =>
  buildRedlinePdf({ study, comparison, redline, onlyChanged, generatedBy: me() }).save(comparePdfFileName("redline", study));

// ---- Sent to coordinators (2026-10-23) ----

/** Live single comparison (coordinators may read it only once it's been sent). */
export const listenComparison = (studyKey, comparisonId, onData, onError) =>
  onSnapshot(
    doc(db, getProtocolComparisonsPath(studyKey), comparisonId),
    (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => onError && onError(err)
  );

/**
 * Admins: send a comparison to coordinators (or stop sending it). One per study
 * at a time: sending a newer one stops the older. The study gets a small
 * pointer field so the coordinators' Studies page can show it; nothing else
 * on the study changes. Recorded in the protocol history.
 * @param {{study: object, studyKey: string, comparison: object, share: boolean}} p
 */
export const shareComparison = async ({ study, studyKey, comparison, share }) => {
  const stamp = { sharedBy: me(), sharedAt: serverTimestamp() };
  const prev = study?.sharedProtocolComparison;
  if (share && prev && prev.comparisonId && prev.comparisonId !== comparison.id) {
    await updateDoc(doc(db, getProtocolComparisonsPath(studyKey), prev.comparisonId), { sharedWithCoordinators: false, ...stamp }).catch(() => {});
  }
  await updateDoc(doc(db, getProtocolComparisonsPath(studyKey), comparison.id), { sharedWithCoordinators: !!share, ...stamp });
  const pointer = share
    ? {
        comparisonId: comparison.id,
        studyKey,
        oldVersion: comparison.old?.version ?? null,
        newVersion: comparison.new?.version ?? null,
        oldFileName: comparison.old?.fileName || "",
        newFileName: comparison.new?.fileName || "",
        // No email here: the study doc is widely readable. Who sent it is on the comparison.
        sharedAt: new Date().toISOString(),
      }
    : null;
  if (study?.id) await updateStudy(study.id, { sharedProtocolComparison: pointer });
  logProtocolEvent(studyKey, {
    type: share ? "comparison_shared" : "comparison_unshared",
    version: comparison.new?.version ?? null,
    detail: comparison.old?.version && comparison.new?.version ? `Version ${comparison.new.version} vs Version ${comparison.old.version}` : "",
  });
  return pointer;
};
